import { sortArrayByName, sortArrayByNameReverse, sortArrayByPage, sortArrayByPageReverse, cloneObject } from "~/fc/Helpers/FormatHelpers"
import { extractThumbnailFromFiles, groupAdjacentFilesByFileId, flattenGroupedFiles, rotateFile, processFilesBeforeThumbGeneration, deleteIndexFromRangeString } from '~/fc/Helpers/ThumbPreviewHelper'
import { createBlankImageDataUrl } from '~/fc/Helpers/PdfHelper'
import { uuidv4 } from "~/fc/Helpers/FileHelpers"
import { convertObjectValuesToRanges } from "~/fc/Helpers/NumericHelpers"
import { parseRangeString } from "~/fc/Helpers/StringHelpers"
import { ROTATION_TEXTS } from "~/fc/constants"

export const state = () => ({
    // Toolbar options on pdf to pdf conversion pages to manipulate the thumbs
    pdfToolBarOptions: {
        viewPages: true,
        include: 'all',
        splitMode: 'custom',
        pageCount: null,
        splitRanges: ['']
    },
    // Thumbs displayed on the merge PDF page
    thumbs: [],
    addThumbsToPosition: null,
    showAddFilesButton: false,
    ranges: null,
    isExtractPagesPdf: false,
    extractPagesRange: '',
    thumbLoading: false,
    thumbDragging: false,
    isPdfPageRemover: false,
})

export const mutations = {
    /**
     * Updates a specific option in the PDF toolbar settings.
     * @param {Object} state - Vuex state object.
     * @param {Object} payload - Contains the key and value to update.
     */
    updatePdfToolBarOption(state, payload) {
        state.pdfToolBarOptions[payload.key] = payload.value
    },
    /**
     * Updates the thumbnails in the state.
     * @param {Object} state - Vuex state object.
     * @param {Array} payload - Array of updated thumbnails.
     */
    updateThumbs(state, payload) {
        state.thumbs = payload
    },
    /**
     * Adds new items to the `state.thumbs` array at a specified position.
     * If `state.addThumbsToPosition` is a valid index, the new items (payload) are inserted 
     * before the item at that index. If invalid, the new items are appended to the array.
     *
     * @param {Object} state - The Vuex state object.
     * @param {Array} payload - The array of items to be added to `state.thumbs`.
     */
    addThumbs(state, payload) {        
        const position = state.addThumbsToPosition;
        let thumbs = cloneObject(state.thumbs);

        const newThumbs = payload.filter(page => {
            const existingPage = thumbs.find(thumb => page.fileId === thumb.fileId && (thumb.page !== 'B' && page.page == thumb.page))
            return !existingPage
        })

        // Insert new thumbs at the correct position
        if (typeof position === 'number' && position >= 0 && position <= thumbs.length) {
            thumbs = [
                ...thumbs.slice(0, position),
                ...newThumbs,
                ...thumbs.slice(position)
            ];
        } else {
            // Add at the end if position is not valid
            thumbs = [...thumbs, ...newThumbs];
        }

        state.thumbs = thumbs;
    },
    /**
     * Updates a specific thumbnail at a given index.
     * @param {Object} state - Vuex state object.
     * @param {Object} payload - Contains the index and updated thumbnail data.
     */
    updateThumbByIndex(state, payload) {
        const thumbs = cloneObject(state.thumbs)
        thumbs[payload.index] = payload.thumb
        state.thumbs = thumbs
    },
    includeThumbsByIndexes(state, indexes) {
        if (!indexes.length) return
        state.thumbs.forEach((thumb) => {
            thumb.include = indexes.includes(thumb.thumbIndex)
        })
    },
    /**
     * Deletes a thumbnail at a specified index.
     * @param {Object} state - Vuex state object.
     * @param {Object} payload - Contains the index of the thumbnail to delete.
     */
    deleteThumbByIndex(state, payload) {
        const thumbs = cloneObject(state.thumbs)
        thumbs.splice(payload.index, 1)

        

        state.thumbs = thumbs
    },
    /**
     * Update addThumbsToPosition
     * @param {Object} state 
     * @param {Object} payload 
     */
    updateAddThumbsToPosition(state, payload) {        
        state.addThumbsToPosition = payload
    },
    /**
     * Adds a new split range at the end of array
     * @param {Object} state 
     * @param {Object} payload 
     */
    addSplitRange(state, payload) {
        const ranges = cloneObject(state.pdfToolBarOptions.splitRanges)
        ranges.push('')
        state.pdfToolBarOptions.splitRanges = ranges
    },
    /**
     * Updates an existing split range by index
     * @param {Object} state 
     * @param {Object} param1 
     */
    updateSplitRange(state, { index, value }) {
        const ranges = cloneObject(state.pdfToolBarOptions.splitRanges)
        ranges[index] = value
        state.pdfToolBarOptions.splitRanges = ranges
    },
    /**
     * Updates all existing split ranges
     * @param {Object} state 
     * @param {Object} param1 
     */
    updateSplitRanges(state, payload) {
        state.pdfToolBarOptions.splitRanges = payload
    },
    /**
     * Deletes an existing split range by index
     * @param {Object} state 
     * @param {Object} param1 
     */
    deleteSplitRange(state, index) {
        const ranges = cloneObject(state.pdfToolBarOptions.splitRanges)
        ranges.splice(index, 1)
        if (ranges.length === 0) ranges.push('')
        state.pdfToolBarOptions.splitRanges = ranges
    },
    updateRanges(state, payload) {
        state.ranges = payload
    },
    resetIndexes(state) {
        const thumbs = cloneObject(state.thumbs)        
        if (state.pdfToolBarOptions.viewPages) {
            thumbs.forEach((thumb, index) => { thumb.thumbIndex = index + 1 })
        } else {
            let index = 0;
            thumbs.forEach(parent => {
                parent.children.forEach(child => {
                    child.thumbIndex = index++;                    
                });
            });
        }
        state.thumbs = thumbs
    },
    updateShowAddFilesButton(state, payload) {
        state.showAddFilesButton = payload
    },
    updatePreviewOfPage(state, payload) {
        const updateThumb = (thumb) => {
            if (thumb.fileId === payload.fileId && thumb.page === payload.page && !thumb.preview) {
                thumb.preview = payload.image;
                thumb.dimension = payload.dimension;
                return true;
            }
            if (thumb.fileId === payload.fileId && thumb.page === payload.page && !thumb.dimension) {
                thumb.dimension = payload.dimension;
                return true;
            }
            return false;
        };

        for (const thumb of state.thumbs) {
            if (updateThumb(thumb)) break;

            if (Array.isArray(thumb.children)) {
                const found = thumb.children.find(updateThumb);
                if (found) break;
            }
        }
    },

    setExtractPagesRange(state, value) {
        state.extractPagesRange = value
    },

    updateExtractPagesPdf(state, value) {
        state.isExtractPagesPdf = value
    },

    updatePdfPageRemover(state, value) {
        state.isPdfPageRemover = value
    },
    
    resetCustomSplitRange(state) {
        state.pdfToolBarOptions.splitRanges = ['']
    },
    resetAll(state) {
        state.thumbs = []
        state.pdfToolBarOptions = {
            viewPages: true,
            include: 'all',
            splitMode: 'custom',
            pageCount: null,
            splitRanges: ['']
        }
        state.addThumbsToPosition = null
        state.showAddFilesButton = null
        state.ranges = null
    },
    setThumbLoading(state, payload) {
        state.thumbLoading = payload
    },
    setThumbDragging(state, payload) {
        state.thumbDragging = payload
    }
}

export const actions = {
    /**
     * Extracts thumbnails from files and updates the state in batches
     * @async
     * @function setThumbsFromFile
     * @param {Object} context - Vuex action context.
     * @param {Object} context.state - Module state.
     * @param {Object} context.rootState - Root Vuex state.
     * @param {Function} context.commit - Vuex commit function.
     * @param {Function} context.dispatch - Vuex dispatch function.
     * 
     * @description
     * - Compares current thumbs with file list to find new files.
     * - Extracts thumbnails from new files.
     * - Adds thumbs in batches to avoid UI blocking.
     */
    async setThumbsFromFile({ state, commit, dispatch, rootState }, file) {
        function processThumbsInBatches(thumbs, state, commit, dispatch, batchSize = 10, startIndex = 0) {
            return new Promise((resolve) => {
                async function nextBatch(index) {
                    const batch = thumbs.slice(index, index + batchSize);
                    
                    // checking if process is complete
                    if (!batch.length) return resolve();

                    // In pdf-page-remover, all thumbs should be unselected initially
                    if (state.isPdfPageRemover) {
                        batch.forEach(thumb => thumb.include = false)
                    }

                    await dispatch('addNewThumbsBasedOnViewPage', batch);
                    commit('updateAddThumbsToPosition', (state.addThumbsToPosition + batch.length));
                    dispatch('autoUpdateInclude');
                    dispatch('resetSplitMode')
                    commit('resetIndexes');
    
                    setTimeout(() => {
                        nextBatch(index + batchSize);
                    }, 200);
                }
    
                nextBatch(startIndex);
            });
        }
        
        let thumbs = await extractThumbnailFromFiles([file]);
        if (thumbs.length) {
            await processThumbsInBatches(thumbs, state, commit, dispatch);
            if(thumbs[0].preview !== false) {
                commit('updateFile', { id: file.id, props: { extracted: true } }, { root: true })
                if (state.isExtractPagesPdf) {
                    if (state.extractPagesRange) {
                        // remove duplicates
                        const indexes = state.thumbs.filter(thumb => thumb.include && thumb.fileId === file.id && !state.extractPagesRange.includes(thumb.thumbIndex)).map(thumb => thumb.thumbIndex);
                        commit('setExtractPagesRange', `${state.extractPagesRange},${indexes.join(',')}`);
                    } else {
                        if ((state.thumbs.length && state.thumbs[0].thumbIndex > 1) || file.importTaskId) {
                            // at the beginning, for any types of cloud upload, we have to reduce 1 from each index
                            const indexes = state.thumbs.filter(thumb => thumb.include && thumb.fileId === file.id).map(thumb => thumb.thumbIndex - 1);
                            commit('setExtractPagesRange', indexes.join(','));
                        } else {
                            const indexes = state.thumbs.filter(thumb => thumb.include && thumb.fileId === file.id).map(thumb => thumb.thumbIndex);
                            commit('setExtractPagesRange', indexes.join(','));
                        }
                    }
                }
            }

            setTimeout(() => {
                if(rootState.list.every(item => item.extracted)) commit('setThumbLoading', false)
            }, 100)
        }
    },
    /**
     * Deletes a specific thumbnail by index.
     * @param {Object} context - Vuex context object.
     * @param {Object} payload - Contains the index of the thumbnail to delete.
     */
    deleteThumb({ state, rootState, commit, dispatch }, { id, softDelete=false }) {
        const index = state.thumbs.findIndex(thumb => thumb.thumbId === id)
        if (!state.thumbs[index]) return
        commit('deleteThumbByIndex', { index })
        if (state.thumbs.length) {
            dispatch('resetSplitMode')
            commit('resetIndexes')
        }
        // else commit('clear', null, { root: true }); // reset the tool
        if(softDelete) return
        // clearing up files whose pages are deleted
        const files = cloneObject(rootState.list)
        const filesWithoutThumbs = files.filter(file => {
            return !state.thumbs.find(thumb => thumb.fileId == file.id);
        });
        if(filesWithoutThumbs && filesWithoutThumbs.length) {
            filesWithoutThumbs.forEach(file => {
                dispatch('removeFile', file.id, {root: true})
            })
        }
    },

    deleteThumbsWithFileIds({ state, commit, dispatch }, batch) {
        const filteredThumbs = structuredClone(state.thumbs).filter(
            thumb => !batch.some(b => b.fileId === thumb.fileId && b.page === thumb.page)
        );

        commit('updateThumbs', filteredThumbs)

        if (state.thumbs.length) {
            dispatch('resetSplitMode')
            commit('resetIndexes')
        }
    },

    deleteThumbsWithFileId({ state, commit, dispatch }, id) {
        const filteredThumbs = structuredClone(state.thumbs).filter(thumb => thumb.fileId != id);
        commit('updateThumbs', filteredThumbs)

        if (state.thumbs.length) {
            dispatch('resetSplitMode')
            commit('resetIndexes')
        }
    },
    /**
     * Creates a copy of the item in `state.thumbs` at the specified index
     * and inserts the copy immediately after the original item. All subsequent
     * items are shifted accordingly.
     *
     * @param {Object} state - The Vuex state object.
     * @param {number} index - The index of the item in `state.thumbs` to copy.
     */
    duplicateThumb({ state, commit, dispatch }, { id }) {
        const index = state.thumbs.findIndex(thumb => thumb.thumbId === id)
        if (typeof index === 'number' && index >= 0 && index < state.thumbs.length) {
            let thumbs = cloneObject(state.thumbs)
            const copy = { ...thumbs[index], thumbId: uuidv4() }; // Create a shallow copy of the item
            if(copy.children) {
                copy.children = copy.children.map(child => ({...child, thumbId: uuidv4()}))
            }
            thumbs = [
                ...thumbs.slice(0, index + 1), // Items before and including the original
                copy,                                 // Add the copy
                ...thumbs.slice(index + 1)    // Items after the original
            ];

            commit('updateThumbs', thumbs)
            dispatch('resetSplitMode')
            commit('resetIndexes')
        }
    },
    /**
     * Updates the view page option and adjusts the thumbnails accordingly.
     * @param {Object} context - Vuex context object.
     * @param {Boolean} value - Whether to enable the view page option.
     */
    updateViewPage({ state, commit }, value) {
        commit('updatePdfToolBarOption', { key: 'viewPages', value: value })
        const items = cloneObject(state.thumbs);
        const thumbs = state.pdfToolBarOptions.viewPages
            ? flattenGroupedFiles(items)
            : groupAdjacentFilesByFileId(items);
        commit('updateThumbs', thumbs)
        commit('resetIndexes')
    },
    /**
     * Updates the view page option and adjusts the thumbnails accordingly.
     * @param {Object} context - Vuex context object.
     * @param {Boolean} value - Whether to enable the view page option.
     */
    addNewThumbsBasedOnViewPage({ state, commit }, newFiles) {
        const thumbs = state.pdfToolBarOptions.viewPages ? newFiles : groupAdjacentFilesByFileId(newFiles)
        commit('addThumbs', thumbs)
    },
    /**
     * Sorts thumbnails by name in ascending or descending order.
     * @param {Object} context - Vuex context object.
     * @param {Boolean} reverse - Whether to sort in reverse order.
     */
    sortThumbsAlphabetic({ state, commit }, reverse) {
        const thumbs = reverse ? sortArrayByNameReverse(cloneObject(state.thumbs)) : sortArrayByName(cloneObject(state.thumbs))
        commit('updateThumbs', thumbs)
        commit('resetIndexes')
    },
    /**
     * Sorts thumbnails by page in ascending or descending order.
     * @param {Object} context - Vuex context object.
     * @param {Boolean} reverse - Whether to sort in reverse order.
     */
    sortThumbByPageNum({ state, commit }, reverse) {
        const thumbs = reverse ? sortArrayByPageReverse(cloneObject(state.thumbs)) : sortArrayByPage(cloneObject(state.thumbs))
        commit('updateThumbs', thumbs)
        commit('resetIndexes')
    },
    /**
     * Toggles the inclusion of a specific thumbnail.
     * @param {Object} context - Vuex context object.
     * @param {Object} payload - Contains the index of the thumbnail and the inclusion status.
     */
    selectThumb({ state, commit, dispatch }, { id, include }) {
        const index = state.thumbs.findIndex(thumb => thumb.thumbId === id)
        if (!state.thumbs[index]) return
        const thumb = cloneObject(state.thumbs)[index]

        if (thumb.children && thumb.children.length) thumb.children = thumb.children.map(child => ({ ...child, include }))
        thumb.include = include

        commit('updateThumbByIndex', { index, thumb })
        dispatch('autoUpdateInclude')
        if (state.isExtractPagesPdf) {
            if (include) {
                commit('setExtractPagesRange', state.extractPagesRange ? `${state.extractPagesRange},${thumb.thumbIndex}` : `${thumb.thumbIndex}`);
            } else {
                commit('setExtractPagesRange', deleteIndexFromRangeString(state.extractPagesRange, thumb.thumbIndex));
            }
        }
    },
    /**
     * Toggles the inclusion of all thumbnails.
     * @param {Object} context - Vuex context object.
     * @param {String} value - "all" to include all, or 'none' to exclude all.
     */
    selectAllThumbs({ state, commit, dispatch }, value) {
        const evenSelection = (thumb, index) => {
            if (state.isExtractPagesPdf || state.isPdfPageRemover) {
                return thumb.page % 2 === 0
            }
            return index % 2 !== 0
        }

        const oddSelection = (thumb, index) => {
            if (state.isExtractPagesPdf || state.isPdfPageRemover) {
                return thumb.page % 2 !== 0
            }
            return index % 2 === 0
        }

        const thumbs = cloneObject(state.thumbs).map((thumb, index) => ({
            ...thumb,
            include: 
                value === 'all' ? true : 
                value === 'none' ? false : 
                value === 'even' ? evenSelection(thumb, index) : 
                value === 'odd' ? oddSelection(thumb, index) : 
                value === 'landscape' ? thumb.dimension.width / thumb.dimension.height > 1 :
                value === 'portrait' ? thumb.dimension.width / thumb.dimension.height < 1 :
                thumb.include
        }));

        commit('updateThumbs', thumbs);
        commit('updatePdfToolBarOption', { key: 'include', value: value })

        if (state.isExtractPagesPdf) {
            const indexes = thumbs.filter(thumb => thumb.include).map(thumb => thumb.thumbIndex);
            commit('setExtractPagesRange', indexes.join(','));
        }
    },
    /**
     * Rotates a specific thumbnail clockwise or counterclockwise.
     * @param {Object} context - Vuex context object.
     * @param {Object} payload - Contains the index and the rotation direction.
     */
    rotateThumb({ state, commit }, { id, isClockWise }) {
        const index = state.thumbs.findIndex(thumb => thumb.thumbId === id)

        if (!state.thumbs[index]) return
        let thumb = cloneObject(state.thumbs)[index]

        if (thumb.children && thumb.children.length) {
            const children = thumb.children.map(child => rotateFile(child, isClockWise))
            const self = rotateFile(thumb, isClockWise)
            thumb = {
                ...self,
                children: children
            }
        } else {
            thumb = rotateFile(thumb, isClockWise)
        }

        commit('updateThumbByIndex', { index, thumb })
    },
    /**
     * Rotates all thumbnails clockwise or counterclockwise, including their children if present.
     * @param {Object} context - Vuex context object.
     * @param {Boolean} isClockWise - Whether to rotate clockwise.
     */
    rotateAllThumbs({ state, commit }, {isClockWise, selectedOnly=false}) {
        const thumbs = cloneObject(state.thumbs).map(thumb => {
            if (thumb.children && thumb.children.length) {
                const children = thumb.children.map(child => (!selectedOnly || child.include) ? rotateFile(child, isClockWise) : child )
                const self = (!selectedOnly || thumb.include) ? rotateFile(thumb, isClockWise) : thumb
                return {
                    ...self,
                    children: children
                }
            } else {
                return (!selectedOnly || thumb.include) ? rotateFile(thumb, isClockWise) : thumb
            }
        })
        commit('updateThumbs', thumbs)
    },
    /**
     * Updates the 'include' option in the PDF toolbar based on the 'include' state of all thumbs.
     * - 'all' if all thumbs are included.
     * - 'none' if no thumbs are included.
     *
     * @param {Object} context - The Vuex action context.
     */
    autoUpdateInclude({ state, commit }) {
        if (state.thumbs.every(thumb => thumb.include)) commit('updatePdfToolBarOption', { key: 'include', value: 'all' })
        else if (!state.thumbs.some(thumb => thumb.include)) commit('updatePdfToolBarOption', { key: 'include', value: 'none' })
        else commit('updatePdfToolBarOption', { key: 'include', value: 'custom' })
    },
    /**
     * Adds a blank page to the thumbs at the specified index.
     * Clones the thumbnail at the provided index and creates a blank image as its preview.
     *
     * @param {Object} context - The Vuex action context.
     * @param {number} index - The position where the blank page should be added.
     */
    async addBlankPage({ state, commit }, { index, position = 'pre' }) {
        commit('updateAddThumbsToPosition', position === 'pre' ? index : index + 1)
        const thumb = cloneObject(state.thumbs)[index - 1] || cloneObject(state.thumbs)[index]
        thumb.preview = await createBlankImageDataUrl(thumb.preview)
        thumb.isBlank = true
        thumb.thumbId = uuidv4()
        thumb.page = 'B'
        commit('addThumbs', [thumb])
        commit('resetIndexes')
    },
    splitThumbsAtIndex({ state, commit }, index) {
        // Clone the state.thumbs array to avoid mutating the original state
        const thumbs = cloneObject(state.thumbs);

        // Get the partition value at the given index
        const newPartition = thumbs[index]?.partition + 1;

        // Update the partition of all elements after the index
        for (let i = index + 1; i < thumbs.length; i++) {
            if(thumbs[i].partition != -1) thumbs[i].partition = thumbs[i].partition + 1;
        }

        // Commit the updated thumbs to the state
        commit('updateThumbs', thumbs);
        commit('resetIndexes')
    },
    joinThumbsAtIndex({ state, commit }, index) {
        // Clone the state.thumbs array to avoid mutating the original state
        const thumbs = cloneObject(state.thumbs);

        // Update the partition of all elements after the index
        for (let i = index + 1; i < thumbs.length; i++) {
            thumbs[i].partition = thumbs[i].partition - 1;
        }

        // Commit the updated thumbs to the state
        commit('updateThumbs', thumbs);

    },
    splitAllThumbs({ state, commit, dispatch }, value) {
        let thumbs = cloneObject(state.thumbs)

        if (value === 'odd_even') {
            thumbs = thumbs
                .map((thumb) => ({ 
                    ...thumb, 
                    partition: (1 - thumb.page % 2),
                    include: true
                }))
                .sort((a, b) => a.partition - b.partition);
        }
        else if (value === 'half') {
            thumbs = thumbs.map((thumb, index) => ({
                ...thumb, 
                partition: index >= thumbs.length / 2 ? 1 : 0,
                include: true
            }))
        }
        else if (value === 'per_pdf' && state.pdfToolBarOptions?.pageCount) {
            const pageCount = state.pdfToolBarOptions.pageCount;
            thumbs = thumbs.map((thumb, index) => ({
                ...thumb,
                partition: Math.floor(index / pageCount),
                include: true
            }));
        }
        else if (value === 'all') {
            thumbs = thumbs.map((thumb, index) => ({
                ...thumb,
                partition: Math.floor(index / 1),
                include: true
            }));
        }

        commit('updateThumbs', thumbs);
        commit('updatePdfToolBarOption', { key: 'splitMode', value: value })
        commit('resetIndexes');
        commit('resetCustomSplitRange')
    },
    movePartition({ state, commit, dispatch }, { fromPartition, toPartition, thumbId, toIndex }) {
        let thumbs = cloneObject(state.thumbs);

        // Find the thumb to be updated
        const thumbToBeUpdatedIndex = thumbs.findIndex(thumb => thumb.thumbId === thumbId);
        if (thumbToBeUpdatedIndex === -1) return;

        const thumbToBeUpdated = thumbs.splice(thumbToBeUpdatedIndex, 1)[0];

        // Update partition value
        if (thumbToBeUpdated.children && thumbToBeUpdated.children.length) thumbToBeUpdated.children = thumbToBeUpdated.children.map(child => ({ ...child, partition: parseInt(toPartition) }))
        thumbToBeUpdated.partition = parseInt(toPartition);
        thumbToBeUpdated.include = toPartition != -1;

        // Find the insertion index
        const insertionIndex = thumbs.findIndex(thumb => thumb.partition == toPartition);
        const finalIndex = insertionIndex !== -1 ? insertionIndex + toIndex : thumbs.length;

        // Insert thumb at the new index
        thumbs.splice(finalIndex, 0, thumbToBeUpdated);

        // Commit the updated thumbs list
        commit('updateThumbs', thumbs);
        dispatch('resetSplitMode')
        commit('resetIndexes')
    },
    /**
     * Rearranges thumbnails by moving an item from one index to another.
     * @param {Object} context - Vuex context object.
     * @param {Object} payload - Contains oldIndex and newIndex.
     */
    moveIndex({ state, commit, dispatch }, { partition, thumbId, toIndex }) {
        let thumbs = cloneObject(state.thumbs);

        // Find the thumb to be updated
        const thumbToBeUpdatedIndex = thumbs.findIndex(thumb => thumb.thumbId === thumbId);
        if (thumbToBeUpdatedIndex === -1) return;

        const thumbToBeUpdated = thumbs.splice(thumbToBeUpdatedIndex, 1)[0];

        // Find the insertion index
        const insertionIndex = thumbs.findIndex(thumb => thumb.partition == partition);
        const finalIndex = insertionIndex !== -1 ? insertionIndex + toIndex : thumbs.length;
        // Insert thumb at the new index
        thumbs.splice(finalIndex, 0, thumbToBeUpdated);
        // Commit the updated thumbs list
        commit('updateThumbs', thumbs);
        dispatch('resetSplitMode')
        dispatch('resetSelectionMode')
        commit('resetIndexes')
    },
    applySplitRange({ state, commit, dispatch }) {
        let thumbs = cloneObject(state.thumbs);
        if (!state.pdfToolBarOptions.viewPages) thumbs = flattenGroupedFiles(thumbs);
        
        let finalThumbs = [];
        const ranges = state.pdfToolBarOptions.splitRanges.map(rangeStr => parseRangeString(rangeStr.trim()));

        finalThumbs = ranges.map((range, rangeIndex) => {
            return range.map(item => {
                const page = thumbs[item-1]
                return {...page, partition: rangeIndex, thumbId: uuidv4(), include: true}
            })
        }).flat();

        if (!state.pdfToolBarOptions.viewPages) {
            finalThumbs = groupAdjacentFilesByFileId(finalThumbs);
        }

        const others = cloneObject(state.thumbs)
            .filter(storeThumb => {
                return !finalThumbs.find(b => ((b.fileId === storeThumb.fileId) && (b.page === storeThumb.page)))
            })
            .map(thumb => ({ ...thumb, include: false, partition: -1 }));

        commit('updateThumbs', [...finalThumbs, ...others]);
        dispatch('resetSplitMode');
        commit('resetIndexes');
    },
    /**
     * Resets SplitMode to 'custom'
     * @param {Object} context - The Vuex action context.
     */
    resetSplitMode({ commit }) {
        commit('updatePdfToolBarOption', { key: 'splitMode', value: 'custom' })
    },
    /**
     * Resets Include to 'custom'
     * @param {Object} context - The Vuex action context.
     */
    resetSelectionMode({ commit }) {
        commit('updatePdfToolBarOption', { key: 'include', value: 'custom' })
    },
    applyCustomInclude({ state, commit }, string) {
        const indexes = parseRangeString(string)
        commit('includeThumbsByIndexes', indexes)
    },
    autoUpdateSplitRanges({ state, commit }) {
        let thumbs = cloneObject(state.thumbs).filter(thumb => thumb.include)
        const splits = thumbs.reduce((acc, { partition, thumbIndex }) => {
            acc[partition] = acc[partition] ? acc[partition] + "," + thumbIndex : "" + thumbIndex;
            return acc;
        }, {});
        commit('updateSplitRanges', Object.values(convertObjectValuesToRanges(splits)))
    },
    /**
     * 
     * @param {*} param
     */
    generateRangeString({ state, commit }) {
        const pagesAreSplit = state.pdfToolBarOptions.viewPages
        let thumbs = cloneObject(state.thumbs).filter(t => t.include);
        if (pagesAreSplit) thumbs = groupAdjacentFilesByFileId(thumbs)        
        const ranges = {}
        thumbs.forEach((thumb, index) => {
            const rangeString = thumb.children?.filter(child => child.include)
                .map(child => {
                    if (child.isBlank) return 'B'
                    if (child.rotation > 0) return child?.page + ROTATION_TEXTS[String(child.rotation)]
                    return child?.page
                }).join(',') || ''            
            if (ranges[`${thumb.partition}:${thumb.fileId}`]) ranges[`${thumb.partition}:${thumb.fileId}_${index}`] = rangeString
            else ranges[`${thumb.partition}:${thumb.fileId}`] = rangeString
        })
        commit('updateRanges', convertObjectValuesToRanges(ranges))
    },
}

export const strict = false
